import { financialYearLabelIsValid } from "@stb/jurisdiction-au/reimbursement"
import { z } from "zod"

import { meRetentionSchema } from "./account.ts"
import { logbookPeriodSchema } from "./logbook-periods.ts"
import { odometerReadingSchema } from "./odometer-readings.ts"
import { tripSchema } from "./trips.ts"
import { createVehicleRequestSchema, vehicleSchema } from "./vehicles.ts"

/**
 * Teams (ADR-016, ADR-017, PRD R8). The enums below are the single source of truth for the
 * database CHECK constraints (apps/web organisations/trips schema) and for request validation.
 * Stored and response schemas never use them: they take plain strings so a value a newer server
 * adds (a new role, a new kind) cannot make an older client fail to decode `/me` or a change.
 */
export const organisationKindSchema = z.enum(["personal", "team"])
export type OrganisationKind = z.infer<typeof organisationKindSchema>

export const organisationMemberRoleSchema = z.enum(["owner", "admin", "member", "accountant"])
export type OrganisationMemberRole = z.infer<typeof organisationMemberRoleSchema>

export const organisationMemberStatusSchema = z.enum(["active", "left"])
export type OrganisationMemberStatus = z.infer<typeof organisationMemberStatusSchema>

export const vehicleOwnershipSchema = z.enum(["company", "member_owned"])
export type VehicleOwnership = z.infer<typeof vehicleOwnershipSchema>

/** Which vehicles a member sees: Owner/Admin `all`, a Member only the `assigned` ones. */
export const vehicleScopeSchema = z.enum(["all", "assigned"])
export type VehicleScope = z.infer<typeof vehicleScopeSchema>

/** `details.reason` of a 403 FORBIDDEN on an organisation-scoped request (ADR-017 2.1). */
export const organisationAccessReasonSchema = z.enum(["not_member", "membership_left"])

/** `details.reason` of a 410 CURSOR_EXPIRED; absent means the retention floor (ADR-017 2.4). */
export const cursorExpiredReasonSchema = z.enum(["access_changed"])

/**
 * One entry of `GET /api/v1/me` `organisations[]` (ADR-017 2.2). Lenient on purpose: `kind`,
 * `role` and `status` are plain strings, and every field except `id` may be missing, so a client
 * that does not know a value skips that organisation instead of failing the whole response.
 * Use `organisationKindSchema` etc. (`safeParse`) to decide whether a value is a known one.
 */
export const meOrganisationEntrySchema = z.object({
  id: z.uuid(),
  name: z.string().default(""),
  kind: z.string(),
  role: z.string(),
  status: z.string(),
  /** Set when `status` is `left`. */
  left_at: z.string().nullable().default(null),
  /** End of the leaver's export window (ADR-016 OQ-4: 12 months after leaving). */
  export_until: z.string().nullable().default(null),
  vehicle_scope: z.string().nullable().default(null),
  /** Snapshot of the server-only assignments, for the vehicle picker (ADR-017 2.2 rule 5). */
  assigned_vehicle_ids: z.array(z.uuid()).nullable().default(null),
  /** The organisation's own entitlements, same shape as `/me.entitlements`; lenient. */
  entitlements: z.record(z.string(), z.unknown()).nullable().default(null),
  /** This organisation's data-retention clock (T1084); null while it is active. */
  retention: meRetentionSchema.nullable().default(null),
})

export type MeOrganisationEntry = z.infer<typeof meOrganisationEntrySchema>

/**
 * Teams server part 1 (T501): team creation, invitations with a written notice and a consent
 * record, member management. Requests are strict; the stored/response schemas below take plain
 * strings and every length is unbounded, so an older or longer stored row still parses.
 */

/** Roles an invitation or a role change may assign. `owner` is never assigned (ADR-016 OQ-6). */
export const teamAssignableRoleSchema = z.enum(["admin", "member", "accountant"])
export type TeamAssignableRole = z.infer<typeof teamAssignableRoleSchema>

export const invitationStatusSchema = z.enum(["pending", "accepted", "revoked"])
export type InvitationStatus = z.infer<typeof invitationStatusSchema>

/** One value per kind of row in the team audit log (CHECK constraint of `team_audit_logs`). */
export const teamAuditActionSchema = z.enum([
  "team_created",
  "invitation_created",
  "invitation_revoked",
  "invitation_accepted",
  "member_role_changed",
  "member_removed",
  "member_left",
  "vehicle_created",
  "vehicle_assigned",
  "vehicle_unassigned",
  // T803: a team vehicle edited, archived or restored (additive; older clients show the raw value).
  "vehicle_updated",
  "vehicle_archived",
  "vehicle_restored",
  // T1298: a team vehicle soft-deleted through sync push (additive; older clients show the raw value).
  "vehicle_deleted",
])
export type TeamAuditAction = z.infer<typeof teamAuditActionSchema>

/** A team name is shown to invitees in an email; a link in it would be a phishing vehicle (T582). */
const LINK_IN_TEXT = /:\/\/|www\.|@|[\p{L}\p{N}-]\.\p{L}{2,}/iu

/** Folds look-alike dots and drops invisible format characters so `crew．com` or `crew\u200B.com` is seen as a link. */
const foldForLinkCheck = (name: string): string =>
  name
    .normalize("NFKC")
    .replaceAll("。", ".")
    .replaceAll(/\p{Cf}/gu, "")

export const createTeamRequestSchema = z.strictObject({
  name: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .refine(
      (name) => !LINK_IN_TEXT.test(foldForLinkCheck(name)),
      "A team name cannot contain a link",
    ),
})
export type CreateTeamRequest = z.infer<typeof createTeamRequestSchema>

export const inviteMemberRequestSchema = z.strictObject({
  email: z.email().max(254),
  role: teamAssignableRoleSchema,
})
export type InviteMemberRequest = z.infer<typeof inviteMemberRequestSchema>

/** `consent` must be literally true: the invitee confirms having read the written notice. */
export const acceptInvitationRequestSchema = z.strictObject({
  token: z.string().min(20).max(200),
  consent: z.literal(true),
  /** The invitee agrees that managers may see their data before the 14-day wait is over. */
  shorten_wait: z.boolean().default(false),
})
export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>

export const updateMemberRoleRequestSchema = z.strictObject({
  role: teamAssignableRoleSchema,
})
export type UpdateMemberRoleRequest = z.infer<typeof updateMemberRoleRequestSchema>

export const teamSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.string(),
  role: z.string(),
})
export type Team = z.infer<typeof teamSchema>

export const teamMemberSchema = z.object({
  user_id: z.uuid(),
  email: z.string().nullable().default(null),
  role: z.string(),
  status: z.string(),
  left_at: z.string().nullable().default(null),
  created_at: z.string(),
})
export type TeamMember = z.infer<typeof teamMemberSchema>

/** `status` is a stored status, or the derived `expired` for a pending invitation past `expires_at`. */
export const teamInvitationSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  expires_at: z.string(),
  notice_version: z.string(),
  notice_sent_at: z.string().nullable().default(null),
  accepted_at: z.string().nullable().default(null),
  /** When managers may start seeing this member's data (ADR-016 G1); null until accepted. */
  manager_access_from: z.string().nullable().default(null),
  created_at: z.string(),
})
export type TeamInvitation = z.infer<typeof teamInvitationSchema>

export const teamAuditEntrySchema = z.object({
  id: z.uuid(),
  action: z.string(),
  actor_user_id: z.uuid().nullable().default(null),
  target_user_id: z.uuid().nullable().default(null),
  old_role: z.string().nullable().default(null),
  new_role: z.string().nullable().default(null),
  /** Set on `invitation_*` rows (T582). */
  invitation_id: z.uuid().nullable().default(null),
  /** Set on `vehicle_*` rows (T502). */
  vehicle_id: z.uuid().nullable().default(null),
  created_at: z.string(),
})
export type TeamAuditEntry = z.infer<typeof teamAuditEntrySchema>

/**
 * The events of the append-only consent log (T426, T1068). The database CHECK
 * `team_consent_records_event_check` holds the same two values.
 */
export const teamConsentEventSchema = z.enum(["notice_sent", "accepted"])
export type TeamConsentEvent = z.infer<typeof teamConsentEventSchema>

/**
 * One row of `GET /api/v1/teams/{orgId}/consent-records` (T1068): who was given the written notice
 * and who agreed, with the notice text version. Lenient like every response schema: `event` is a
 * plain string so a future event never turns the list into an error. No names or e-mail addresses.
 */
export const teamConsentRecordSchema = z.object({
  id: z.uuid(),
  invitation_id: z.uuid(),
  /** See `teamConsentEventSchema`. */
  event: z.string(),
  /** Version id of the notice text that was sent or accepted. */
  notice_version: z.string(),
  /** `notice_sent`: the manager who invited. `accepted`: the person who agreed. */
  actor_user_id: z.uuid(),
  /** Only on `accepted`: the invitee agreed to shorten the 14-day wait; otherwise null. */
  shorten_wait: z.boolean().nullable().default(null),
  recorded_at: z.string(),
})
export type TeamConsentRecord = z.infer<typeof teamConsentRecordSchema>

/**
 * Paging of the consent log, newest first (T1110): the first page is the newest `limit` rows (200
 * by default, so a caller without parameters gets the same answer as before). To read on, send
 * `before` and `before_id` (both, or neither) of the last row received (`recorded_at` has millisecond precision), until a
 * page holds fewer rows than `limit`. Additive: older clients never send these.
 */
export const teamConsentRecordQuerySchema = z
  .strictObject({
    limit: z.coerce.number().int().min(1).max(500).default(200),
    before: z.iso.datetime({ offset: true }).optional(),
    before_id: z.uuid().optional(),
  })
  // A time alone would skip rows that share a millisecond, and this log must stay fully readable.
  .refine((q) => (q.before === undefined) === (q.before_id === undefined), {
    message: "before and before_id go together",
    path: ["before_id"],
  })
export type TeamConsentRecordQuery = z.input<typeof teamConsentRecordQuerySchema>

/**
 * Teams server part 2 (T502): team vehicles and their assignment to members (ADR-016 2.3).
 * `company` vehicles are assigned to a Member with a seat; a `member_owned` vehicle belongs to
 * `owner_user_id` and is never assigned to anyone else.
 */
export const createTeamVehicleRequestSchema = z
  .strictObject({
    vehicle: createVehicleRequestSchema,
    ownership: vehicleOwnershipSchema.default("company"),
    owner_user_id: z.uuid().optional(),
  })
  .refine((body) => (body.ownership === "member_owned") === (body.owner_user_id !== undefined), {
    message: "owner_user_id is required for member_owned vehicles and not allowed for company ones",
    path: ["owner_user_id"],
  })
export type CreateTeamVehicleRequest = z.input<typeof createTeamVehicleRequestSchema>

export const assignVehicleRequestSchema = z.strictObject({ user_id: z.uuid() })
export type AssignVehicleRequest = z.infer<typeof assignVehicleRequestSchema>

/** One assignment; `unassigned_at` is null while it is in force (cancelling affects the future only). */
export const vehicleAssignmentSchema = z.object({
  id: z.uuid(),
  vehicle_id: z.uuid(),
  user_id: z.uuid(),
  assigned_by_user_id: z.uuid().nullable().default(null),
  assigned_at: z.string(),
  unassigned_at: z.string().nullable().default(null),
})
export type VehicleAssignment = z.infer<typeof vehicleAssignmentSchema>

/**
 * `GET /teams/{id}/vehicles` (T508): the vehicles the caller may see (Owner/Admin: all, archived
 * included; Member: the ones assigned to them or their own) with the assignments in force.
 */
export const teamVehicleListSchema = z.object({
  vehicles: z.array(vehicleSchema),
  assignments: z.array(vehicleAssignmentSchema),
  /** T803: the logbook period running now (`status` active) on each listed vehicle; none otherwise. */
  logbook_periods: z.array(logbookPeriodSchema).default([]),
})
export type TeamVehicleList = z.infer<typeof teamVehicleListSchema>

/**
 * Team vehicle logbook (T502, ADR-016 2.4-2.7): one vehicle over whole calendar months. Query
 * parameters are strict: `from`/`to` are `YYYY-MM` (inclusive, at most 24 months), `before` pages
 * by trip start time.
 */
const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)
export const teamLogbookQuerySchema = z
  .strictObject({
    from: monthSchema.optional(),
    to: monthSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
    before: z.iso.datetime({ offset: true }).optional(),
    /** Id of the last trip of the previous page; with `before` it makes the cursor exact on ties. */
    before_id: z.uuid().optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: "from must not be after to",
    path: ["from"],
  })
export type TeamLogbookQuery = z.input<typeof teamLogbookQuerySchema>

/**
 * What the caller may see. Owner/Admin: every trip a member classified `business` (once that
 * member's 14-day wait is over), their own trips, and `private_km_by_month` (a monthly private-only
 * total, never who/when/where; unclassified km are not counted).
 * Member: only their own trips (private ones included) and no private total.
 */
export const teamVehicleLogbookSchema = z.object({
  vehicle: vehicleSchema,
  periods: z.array(logbookPeriodSchema),
  odometer_readings: z.array(odometerReadingSchema),
  trips: z.array(tripSchema),
  has_more: z.boolean().default(false),
  from: z.string(),
  to: z.string(),
  private_km_by_month: z.array(z.object({ month: z.string(), distance_m: z.number() })),
})
export type TeamVehicleLogbook = z.infer<typeof teamVehicleLogbookSchema>

/**
 * Paging of the access log, newest first: `before`/`before_id` are the last entry of the previous
 * page (`viewed_at` has millisecond precision). Send both: a (time, id) cursor; `before` alone
 * is the old cursor and can skip rows with an equal timestamp.
 */
export const teamAccessLogQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(500).default(200),
  before: z.iso.datetime({ offset: true }).optional(),
  before_id: z.uuid().optional(),
})
export type TeamAccessLogQuery = z.input<typeof teamAccessLogQuerySchema>

/** Who looked at whose trips (ADR-016 G4/G5). `object_type` is `trip_list`, `trip` or `export`. */
export const teamAccessLogEntrySchema = z.object({
  id: z.uuid(),
  viewer_user_id: z.uuid().nullable().default(null),
  /** T1148: the viewer's profile name (never email or phone); null when none or the viewer was erased. */
  viewer_name: z.string().nullable().default(null),
  /** T1148: the viewer's team role (`owner`, `admin`, ...), the role they hold now, or their last role if they left (not the role at view time); null only if the membership row is gone. */
  viewer_role: z.string().nullable().default(null),
  subject_user_id: z.uuid().nullable().default(null),
  object_type: z.string(),
  object_id: z.uuid().nullable().default(null),
  viewed_at: z.string(),
})
export type TeamAccessLogEntry = z.infer<typeof teamAccessLogEntrySchema>

/**
 * Team report (T504, ADR-016 2.4, 2.5): per vehicle and calendar month, only trips the recorder
 * classified `business` (private and unclassified trips never count, and no unclassified total
 * exists). `private_distance_m` is the whole-month private total for Owner/Admin and `null` for
 * everyone else. Vehicles page by id: `after` is the last `vehicle_id` of the previous page. The
 * data carries no amounts, so there is no money field.
 */
export const teamReportQuerySchema = z
  .strictObject({
    from: monthSchema.optional(),
    to: monthSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    after: z.uuid().optional(),
    /** T803: report on this one vehicle only (the vehicle page). */
    vehicle_id: z.uuid().optional(),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: "from must not be after to",
    path: ["from"],
  })
export type TeamReportQuery = z.input<typeof teamReportQuerySchema>

export const teamReportMonthSchema = z.object({
  month: z.string(),
  business_distance_m: z.number(),
  business_trip_count: z.number(),
  private_distance_m: z.number().nullable().default(null),
})
export const teamReportVehicleSchema = z.object({
  vehicle: vehicleSchema,
  months: z.array(teamReportMonthSchema),
  business_distance_m: z.number(),
  business_trip_count: z.number(),
})
export const teamReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  vehicles: z.array(teamReportVehicleSchema),
  has_more: z.boolean().default(false),
})
export type TeamReport = z.infer<typeof teamReportSchema>

/**
 * Employee-owned cars: what the employer reimburses (T446, ADR-016 M2, PRD R8.16). The rate is
 * cents per km and is the employer's data per income year; with no row for a year the ATO
 * approved rate from `@stb/jurisdiction-au` applies (`source: "ato"`). Amounts are integer minor
 * units (`*_minor`) in the report's `currency`. The ATO 5,000 km cap is per car and per income
 * year, so the first 5,000 km and the rest are reported apart. An estimate for the employer's
 * books, not tax advice.
 */
export const reimbursementFinancialYearSchema = z
  .string()
  .refine(financialYearLabelIsValid, "financial_year must look like 2026-27")

/** Upper bound of a rate an employer can set (A$100 per km); a typo guard, not a tax rule. */
export const REIMBURSEMENT_MAX_CENTS_PER_KM = 10_000

export const setReimbursementRateRequestSchema = z.strictObject({
  cents_per_km: z.number().int().min(1).max(REIMBURSEMENT_MAX_CENTS_PER_KM),
  /** Where the employer's rate comes from, e.g. an award clause. */
  note: z.string().trim().max(200).nullable().optional(),
})
export type SetReimbursementRateRequest = z.input<typeof setReimbursementRateRequestSchema>

export const teamReimbursementQuerySchema = z.strictObject({
  financial_year: reimbursementFinancialYearSchema,
  /** Owner/Admin/Accountant: narrow the report to one member. Ignored for a Member (own data only). */
  user_id: z.uuid().optional(),
})
export type TeamReimbursementQuery = z.input<typeof teamReimbursementQuerySchema>

/** One income year's rate. `source` is `team` (the employer's) or `ato` (the default table). */
export const teamReimbursementRateSchema = z.object({
  financial_year: z.string(),
  cents_per_km: z.number(),
  currency: z.string().default("AUD"),
  source: z.string(),
  note: z.string().nullable().default(null),
  /** The ATO page the `ato` default was read from; null for a team rate. */
  source_url: z.string().nullable().default(null),
  updated_at: z.string().nullable().default(null),
})
export type TeamReimbursementRate = z.infer<typeof teamReimbursementRateSchema>

export const teamReimbursementRatesSchema = z.object({
  rates: z.array(teamReimbursementRateSchema),
})
export type TeamReimbursementRates = z.infer<typeof teamReimbursementRatesSchema>

/** One car: business metres split at the 5,000 km cap. Amounts are null while the year has no rate. */
export const teamReimbursementVehicleSchema = z.object({
  vehicle: vehicleSchema,
  business_distance_m: z.number(),
  business_trip_count: z.number(),
  within_cap_distance_m: z.number(),
  over_cap_distance_m: z.number(),
  within_cap_minor: z.number().nullable().default(null),
  over_cap_minor: z.number().nullable().default(null),
  total_minor: z.number().nullable().default(null),
})
export type TeamReimbursementVehicle = z.infer<typeof teamReimbursementVehicleSchema>

export const teamReimbursementMemberSchema = z.object({
  user_id: z.uuid(),
  /** The sign-in profile name; null when none. Never an e-mail address. */
  name: z.string().nullable().default(null),
  vehicles: z.array(teamReimbursementVehicleSchema),
  business_distance_m: z.number(),
  within_cap_distance_m: z.number(),
  over_cap_distance_m: z.number(),
  within_cap_minor: z.number().nullable().default(null),
  over_cap_minor: z.number().nullable().default(null),
  total_minor: z.number().nullable().default(null),
})
export type TeamReimbursementMember = z.infer<typeof teamReimbursementMemberSchema>

export const teamReimbursementReportSchema = z.object({
  financial_year: z.string(),
  currency: z.string().default("AUD"),
  /** Km per car and year the ATO treats differently (informational; 5,000). */
  cap_km: z.number().default(5000),
  /** The rate used; null when the year has neither a team rate nor an ATO default. */
  rate: teamReimbursementRateSchema.nullable().default(null),
  /** The ATO cents per km deduction rate of the year from jurisdiction-au (null when we carry none) and whether the employer rate is higher. Whether it equals the PAYG approved rate is unverified (T445 item 2): no withholding verdict is drawn from it. */
  ato_cents_per_km: z.number().nullable().default(null),
  rate_above_ato: z.boolean().default(false),
  members: z.array(teamReimbursementMemberSchema),
  within_cap_minor: z.number().nullable().default(null),
  over_cap_minor: z.number().nullable().default(null),
  total_minor: z.number().nullable().default(null),
})
export type TeamReimbursementReport = z.infer<typeof teamReimbursementReportSchema>

/**
 * Sync entities a team organisation has (ADR-017 2.7, OQ-17-4): the logbook domain only. Invoices
 * and receipts are not enabled for teams, so asking for them is a 422 `domain_not_enabled`.
 */
export const TEAM_SYNC_ENTITIES = [
  "trips",
  "vehicles",
  "odometer_readings",
  "trip_tracks",
  "logbook_periods",
] as const
